import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Users, Mail, Phone, Search, UserRound, Pencil, X, Save } from "lucide-react";

export default function TournamentDetail() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [name, setName] = useState("");
  const [playerInput, setPlayerInput] = useState("");
  const [players, setPlayers] = useState([]);
  const [playerMatches, setPlayerMatches] = useState([]);
  const [playerLookupLoading, setPlayerLookupLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Edit Team State
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamPlayers, setEditTeamPlayers] = useState([]);
  const [editPlayerInput, setEditPlayerInput] = useState("");
  const [editPlayerMatches, setEditPlayerMatches] = useState([]);
  const [editPlayerLookupLoading, setEditPlayerLookupLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError("");
    api.get(`/tournaments/${tournamentId}`)
      .then((r) => {
        if (!alive) return;
        const detail = r.data || {};
        setTournament(detail);
      })
      .catch((err) => {
        if (!alive) return;
        console.warn("[tournament-detail] load failed", err);
        setLoadError(err?.response?.data?.detail || "Unable to load tournament details");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [tournamentId]);

  const normalizedIdentifier = (value) => value.trim().toLowerCase().replace(/\D/g, "");

  const isPlayerAlreadyRegistered = (playerId, currentEditingTeamId = null) => {
    if (!tournament?.teams?.length) return false;
    return tournament.teams.some((team) => {
      // Ignore current team being edited
      if (currentEditingTeamId && team.id === currentEditingTeamId) return false;
      return (team.players || []).some((player) => player.id === playerId);
    });
  };

  const addResolvedPlayer = (candidate, isEdit = false) => {
    if (!candidate?.id) {
      toast.error("Player could not be resolved from the database");
      return;
    }

    const currentList = isEdit ? editTeamPlayers : players;

    if (currentList.some((p) => p.id === candidate.id)) {
      toast.error("This player is already in the team list");
      return;
    }

    if (isPlayerAlreadyRegistered(candidate.id, isEdit ? editingTeamId : null)) {
      toast.error("This player is already registered for another team in this tournament");
      return;
    }

    const formattedCandidate = {
      id: candidate.id,
      identifier: candidate.email || candidate.phone || candidate.name || "",
      name: candidate.name || "Unnamed",
      email: candidate.email || "",
      phone: candidate.phone || "",
      position: candidate.position || "",
      location: candidate.location || "",
    };

    if (isEdit) {
      setEditTeamPlayers((prev) => [...prev, formattedCandidate]);
      setEditPlayerInput("");
      setEditPlayerMatches([]);
    } else {
      setPlayers((prev) => [...prev, formattedCandidate]);
      setPlayerInput("");
      setPlayerMatches([]);
    }

    toast.success(`${candidate.name || "Player"} added to team`);
  };

  const addPreviewPlayer = async (isEdit = false) => {
    const inputVal = isEdit ? editPlayerInput : playerInput;
    const matchesVal = isEdit ? editPlayerMatches : playerMatches;
    const trimmed = inputVal.trim();
    if (!trimmed) return;

    const exactMatch = matchesVal.find((player) => {
      const exactEmail = (player.email || "").toLowerCase() === trimmed.toLowerCase();
      const exactPhone = normalizedIdentifier(player.phone || "") === normalizedIdentifier(trimmed);
      return exactEmail || exactPhone;
    });

    if (exactMatch) {
      addResolvedPlayer(exactMatch, isEdit);
      return;
    }

    if (isEdit) setEditPlayerLookupLoading(true);
    else setPlayerLookupLoading(true);

    try {
      const { data } = await api.get("/players/search", {
        params: { search: trimmed, page: 1, limit: 5 },
      });
      const results = data?.players || [];
      const found = results.find((player) => {
        const exactEmail = (player.email || "").toLowerCase() === trimmed.toLowerCase();
        const exactPhone = normalizedIdentifier(player.phone || "") === normalizedIdentifier(trimmed);
        return exactEmail || exactPhone;
      });

      if (!found) {
        toast.error("Player not found in the database. Use an existing email or mobile number.");
        return;
      }

      addResolvedPlayer(found, isEdit);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to resolve player");
    } finally {
      if (isEdit) setEditPlayerLookupLoading(false);
      else setPlayerLookupLoading(false);
    }
  };

  const removePreviewPlayer = (id, isEdit = false) => {
    if (isEdit) {
      setEditTeamPlayers((prev) => prev.filter((p) => p.id !== id));
    } else {
      setPlayers((prev) => prev.filter((p) => p.id !== id));
    }
  };

  // Debounced Search for "Add Team"
  useEffect(() => {
    const trimmed = playerInput.trim();
    if (trimmed.length < 3) {
      setPlayerMatches([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setPlayerLookupLoading(true);
        const { data } = await api.get("/players/search", { params: { search: trimmed, page: 1, limit: 5 } });
        const results = data?.players || [];
        const filtered = results.filter((player) => player.id && (player.email || player.phone));
        setPlayerMatches(filtered);
      } catch (err) {
        console.warn("[tournament-detail] player lookup failed", err);
        setPlayerMatches([]);
      } finally {
        setPlayerLookupLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [playerInput]);

  // Debounced Search for "Edit Team"
  useEffect(() => {
    const trimmed = editPlayerInput.trim();
    if (trimmed.length < 3) {
      setEditPlayerMatches([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setEditPlayerLookupLoading(true);
        const { data } = await api.get("/players/search", { params: { search: trimmed, page: 1, limit: 5 } });
        const results = data?.players || [];
        const filtered = results.filter((player) => player.id && (player.email || player.phone));
        setEditPlayerMatches(filtered);
      } catch (err) {
        console.warn("[tournament-detail] player lookup failed", err);
        setEditPlayerMatches([]);
      } finally {
        setEditPlayerLookupLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [editPlayerInput]);

  const saveTeam = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Team name is required");
      return;
    }
    if (players.length === 0) {
      toast.error("Add at least one player");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/tournaments/${tournamentId}/teams`, {
        name,
        player_entries: players,
      });
      setName("");
      setPlayers([]);
      setTournament((prev) => ({
        ...prev,
        teams: [...(prev?.teams || []), data],
      }));
      toast.success("Team added successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add team");
    } finally {
      setLoading(false);
    }
  };

  const startEditingTeam = (team) => {
    setEditingTeamId(team.id);
    setEditTeamName(team.name || "");
    setEditTeamPlayers(Array.isArray(team.players) ? [...team.players] : []);
    setEditPlayerInput("");
    setEditPlayerMatches([]);
  };

  const cancelEditingTeam = () => {
    setEditingTeamId(null);
    setEditTeamName("");
    setEditTeamPlayers([]);
    setEditPlayerInput("");
    setEditPlayerMatches([]);
  };

  const handleUpdateTeam = async (e) => {
  e.preventDefault();
  if (!editTeamName.trim()) {
    toast.error("Team name is required");
    return;
  }
  if (editTeamPlayers.length === 0) {
    toast.error("Add at least one player to the team");
    return;
  }

  // Format player entries to match the TeamCreate schema expectation
  const formattedPlayerEntries = editTeamPlayers.map((p) => ({
    identifier: p.email || p.phone || p.identifier || p.id,
  }));

  setUpdateLoading(true);
  try {
    const { data } = await api.put(
      `/tournaments/${tournamentId}/teams/${editingTeamId}`,
      {
        name: editTeamName,
        player_entries: formattedPlayerEntries,
      }
    );

    setTournament((prev) => ({
      ...prev,
      teams: (prev?.teams || []).map((t) => (t.id === editingTeamId ? data : t)),
    }));

    toast.success("Team updated successfully");
    cancelEditingTeam();
  } catch (err) {
    const detail = err?.response?.data?.detail;
    const msg = Array.isArray(detail)
      ? detail.map((e) => e.msg).join(", ")
      : typeof detail === "string"
      ? detail
      : "Failed to update team";
      
    toast.error(msg);
  } finally {
    setUpdateLoading(false);
  }
};

  const canAddTeam = Boolean(user) && (user?.role === "admin" || tournament?.organizer_id === user?.id);
  const teams = Array.isArray(tournament?.teams) ? tournament.teams : [];
  const inheritedFormat = tournament?.format || "5x5";
  const selectedPlayerCount = players.length;

  const suggestedMatch = useMemo(() => {
    const trimmed = playerInput.trim().toLowerCase();
    if (!trimmed) return null;
    return playerMatches.find((player) => {
      const email = (player.email || "").toLowerCase();
      const phone = (player.phone || "").replace(/\D/g, "");
      return email.includes(trimmed) || phone.includes(trimmed.replace(/\D/g, ""));
    }) || null;
  }, [playerInput, playerMatches]);

  const editSuggestedMatch = useMemo(() => {
    const trimmed = editPlayerInput.trim().toLowerCase();
    if (!trimmed) return null;
    return editPlayerMatches.find((player) => {
      const email = (player.email || "").toLowerCase();
      const phone = (player.phone || "").replace(/\D/g, "");
      return email.includes(trimmed) || phone.includes(trimmed.replace(/\D/g, ""));
    }) || null;
  }, [editPlayerInput, editPlayerMatches]);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Loading tournament…</div>;
  }

  if (loadError || !tournament) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="border border-zinc-800 p-6 text-zinc-400">
          {loadError || "Tournament not found."}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
      <div className="border border-zinc-800 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Tournament</div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-2">{tournament.name || "Untitled Tournament"}</h1>
            <p className="text-sm text-zinc-400 mt-2">{tournament.location || "Unknown location"} · {tournament.start_date || "TBD"} → {tournament.end_date || "TBD"}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-300">
              <span className="border border-zinc-800 px-2 py-1">Format: {tournament.format || "5x5"}</span>
              <span className="border border-zinc-800 px-2 py-1">Age: {(tournament.age_categories || []).join(", ") || "Open"}</span>
            </div>
          </div>
          <Link to={`/tournaments/${tournamentId}/games`} className="inline-flex items-center gap-2 border border-court/60 px-4 py-2 text-[11px] font-bold tracking-[0.25em] uppercase text-court hover:text-white hover:border-court transition-colors">
            <Plus className="w-4 h-4" /> Games
          </Link>
        </div>
      </div>

      {canAddTeam && (
        <form onSubmit={saveTeam} className="border border-zinc-800 p-6 space-y-4">
          <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Add Team</div>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Team Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mt-2 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
                placeholder="Court Kings"
              />
            </label>
            <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-400 md:col-span-2">
              Tournament format is fixed to <span className="font-bold text-court">{inheritedFormat}</span> for this team add flow.
            </div>
          </div>

          <label className="block">
            <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Add players by email or mobile</span>
            <div className="mt-2 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  value={playerInput}
                  onChange={(e) => setPlayerInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addPreviewPlayer(false);
                    }
                  }}
                  placeholder="player@example.com or 9999999999"
                  className="w-full pl-9 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
                />
              </div>
              <button type="button" onClick={() => addPreviewPlayer(false)} disabled={playerLookupLoading} className="h-11 px-4 border border-court text-court text-xs font-bold tracking-widest uppercase disabled:opacity-60">
                {playerLookupLoading ? "Checking…" : "Add"}
              </button>
            </div>
          </label>

          {suggestedMatch && (
            <button
              type="button"
              onClick={() => addResolvedPlayer(suggestedMatch, false)}
              className="w-full text-left rounded border border-court/40 bg-court/10 p-3 hover:bg-court/20 hover:border-court transition-colors cursor-pointer group"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-court">Player found (Click to add)</div>
                  <div className="mt-1 font-display font-black text-xl uppercase text-white group-hover:text-court transition-colors">
                    {suggestedMatch.name || "Unnamed"}
                  </div>
                  <div className="text-xs text-zinc-300 flex flex-wrap gap-2 mt-1">
                    {suggestedMatch.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{suggestedMatch.email}</span>}
                    {suggestedMatch.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{suggestedMatch.phone}</span>}
                  </div>
                </div>
              </div>
            </button>
          )}

          {playerMatches.length > 0 && !suggestedMatch && (
            <div className="grid gap-2">
              {playerMatches.map((player) => (
                <button key={player.id} type="button" onClick={() => addResolvedPlayer(player, false)} className="w-full text-left border border-zinc-800 hover:border-court p-3 bg-zinc-950/50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display font-black text-lg uppercase text-white">{player.name || "Unnamed"}</div>
                      <div className="text-xs text-zinc-400 mt-1">{player.email || player.phone}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-court">Select</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedPlayerCount > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Team Members</div>
              <div className="grid gap-2">
                {players.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 border border-zinc-800 p-3">
                    <button type="button" onClick={() => navigate(`/players/${p.id}/portfolio`)} className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-court/20 border border-court/50 flex items-center justify-center text-court"><UserRound className="w-4 h-4" /></div>
                        <div>
                          <div className="font-display font-black uppercase text-lg text-white">{p.name || "Unnamed"}</div>
                          <div className="text-xs text-zinc-400">{p.email || p.phone}</div>
                        </div>
                      </div>
                    </button>
                    <button type="button" onClick={() => removePreviewPlayer(p.id, false)} className="text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button type="submit" disabled={loading || selectedPlayerCount === 0} className="h-12 px-5 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50">
            {loading ? "Saving…" : `Save Team (${selectedPlayerCount})`}
          </button>
        </form>
      )}

      <section className="border border-zinc-800 p-6">
        <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.3em] uppercase text-court">
          <Users className="w-4 h-4" /> Registered Teams
        </div>
        {!teams.length ? (
          <div className="text-sm text-zinc-500 mt-4">No teams added yet.</div>
        ) : (
          <div className="mt-4 space-y-4">
            {teams.map((team) => {
              const isEditingThisTeam = editingTeamId === team.id;
              const playersForTeam = Array.isArray(team.players) ? team.players : [];

              if (isEditingThisTeam) {
                return (
                  <form key={team.id} onSubmit={handleUpdateTeam} className="border-2 border-court bg-zinc-950 p-5 space-y-4">
                    <div className="flex items-center justify-between gap-2 border-b border-zinc-800 pb-3">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-court">Editing Team</div>
                      <button type="button" onClick={cancelEditingTeam} className="text-zinc-400 hover:text-white p-1">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <label className="block">
                      <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Team Name</span>
                      <input
                        value={editTeamName}
                        onChange={(e) => setEditTeamName(e.target.value)}
                        className="w-full mt-2 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
                        placeholder="Team Name"
                      />
                    </label>

                    <label className="block">
                      <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Add players to this team</span>
                      <div className="mt-2 flex gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            value={editPlayerInput}
                            onChange={(e) => setEditPlayerInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addPreviewPlayer(true);
                              }
                            }}
                            placeholder="player@example.com or 9999999999"
                            className="w-full pl-9 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
                          />
                        </div>
                        <button type="button" onClick={() => addPreviewPlayer(true)} disabled={editPlayerLookupLoading} className="h-11 px-4 border border-court text-court text-xs font-bold tracking-widest uppercase disabled:opacity-60">
                          {editPlayerLookupLoading ? "Checking…" : "Add"}
                        </button>
                      </div>
                    </label>

                    {editSuggestedMatch && (
                      <button
                        type="button"
                        onClick={() => addResolvedPlayer(editSuggestedMatch, true)}
                        className="w-full text-left rounded border border-court/40 bg-court/10 p-3 hover:bg-court/20 hover:border-court transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-court">Player found (Click to add)</div>
                            <div className="mt-1 font-display font-black text-xl uppercase text-white group-hover:text-court transition-colors">
                              {editSuggestedMatch.name || "Unnamed"}
                            </div>
                            <div className="text-xs text-zinc-300 flex flex-wrap gap-2 mt-1">
                              {editSuggestedMatch.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{editSuggestedMatch.email}</span>}
                              {editSuggestedMatch.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{editSuggestedMatch.phone}</span>}
                            </div>
                          </div>
                        </div>
                      </button>
                    )}

                    {editPlayerMatches.length > 0 && !editSuggestedMatch && (
                      <div className="grid gap-2">
                        {editPlayerMatches.map((player) => (
                          <button key={player.id} type="button" onClick={() => addResolvedPlayer(player, true)} className="w-full text-left border border-zinc-800 hover:border-court p-3 bg-zinc-950/50 transition-colors">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="font-display font-black text-lg uppercase text-white">{player.name || "Unnamed"}</div>
                                <div className="text-xs text-zinc-400 mt-1">{player.email || player.phone}</div>
                              </div>
                              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-court">Select</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Roster ({editTeamPlayers.length})</div>
                      <div className="grid gap-2">
                        {editTeamPlayers.map((p) => (
                          <div key={p.id} className="flex items-center justify-between gap-3 border border-zinc-800 p-2.5 bg-zinc-900/50">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-court/20 border border-court/50 flex items-center justify-center text-court"><UserRound className="w-3.5 h-3.5" /></div>
                              <div>
                                <div className="font-bold uppercase text-sm text-white">{p.name || "Unnamed"}</div>
                                <div className="text-[11px] text-zinc-400">{p.email || p.phone}</div>
                              </div>
                            </div>
                            <button type="button" onClick={() => removePreviewPlayer(p.id, true)} className="text-xs font-bold uppercase tracking-widest text-red-400 hover:text-red-300">Remove</button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <button type="submit" disabled={updateLoading || editTeamPlayers.length === 0} className="inline-flex items-center gap-2 h-10 px-4 bg-court text-white font-bold text-xs uppercase tracking-widest hover:bg-court-hover transition-colors disabled:opacity-50">
                        <Save className="w-4 h-4" /> {updateLoading ? "Saving…" : "Save Changes"}
                      </button>
                      <button type="button" onClick={cancelEditingTeam} className="h-10 px-4 border border-zinc-800 text-zinc-400 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                );
              }

              return (
                <div key={team.id} className="border border-zinc-800 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display font-black text-2xl uppercase tracking-tight">{team.name}</div>
                      <div className="text-xs text-zinc-500 mt-1">Format: {team.format || "5x5"}</div>
                    </div>
                    {canAddTeam && (
                      <button
                        type="button"
                        onClick={() => startEditingTeam(team)}
                        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-court px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-court transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit Team
                      </button>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {playersForTeam.map((player) => (
                      <div key={player.id} className="border border-zinc-800 px-3 py-2 text-sm">
                        <div className="font-bold text-white">{player.name || "Unnamed"}</div>
                        <div className="text-zinc-500 text-xs flex gap-2 flex-wrap">
                          {player.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{player.email}</span>}
                          {player.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{player.phone}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}